/// <reference path="../../../../../../typings/index.d.ts" />


module Meevo.Plugins.Accountsync {
    'use strict';

    import ObjectStateEnum = Meevo.Model.ObjectStateEnum;

    enum MainTabScreens {
        Welcome,
        DeleteConnection,
        EditConnection,
        ConnectionForm
    }

    enum ConnectionFormSteps {
        ConnectionCredentials,
        AccountSetup,
        PaymentTypeSetup,
        ReadyToSend
    }

    enum ToolTipEnum {
        ProductIncome,
        ServiceIncome,
        OtherIncome,
        AccountsReceivable,
        InventoryAssets,
        CostofGoodsSoldAccount,
        Liabilities,
        ExpenseAccount
    }
    
    interface IAccountsRetentionStatus {
        entityDisplayName: string;
        messages?: string[];
    }

    interface IAccountToUseList {
        id: string;
        displayName: string;
    }

    interface IToolTip {
        value: ToolTipEnum,
        template: string
    }

    interface IAccountsyncMainController {
        screenTitle: string,
        currentScreen: MainTabScreens,
        currentConnectionFormStep: ConnectionFormSteps,
        lastConnectionFormStep: ConnectionFormSteps,

        latestConnectionId: Guid,
        selectedConnectionListItem: IConnectionListItem,
        selectedConnection: IConnectionSetupInfo & IAccountsSetup,
        selectedConnectionBackup: IConnectionSetupInfo & IAccountsSetup,

        connectionList: IConnectionListItem[],
        accountSystemList: IAccountSystemList[][],
        defaultAcountsList: IAccountInfo[],
        externalAccountList: IAccountInfo[][],
        paymentAccountList: IPaymentAccount[][],
        paymentTypes: Meevo.Model.PaymentTypeListItem[],
        accountsRetentionStatus: Array<IAccountsRetentionStatus>,

        isDisableControlButtons: boolean,
        isRemoveSuccessful: boolean,
        isRemovedFirstConnection: boolean,
        isAccountOptionsSavedSuccessfully: boolean,
        isShowConnectionCredentials: boolean,
        isSuccessfullyRefreshedToken: boolean,
        isSuccessfullyAuthInQb: boolean,
        isRealmIdUnique: boolean,

        defaultPaymentAccountName: string;
        defaultAccountCountRequestBody: IAccountCountRequest,
        toolTips: IToolTip[],

        showScreen: (screen: MainTabScreens) => void,
        goToNextStep: () => void,
        getSelectedAccount: (accountType: IAccountSystemList) => string,
        openConnectionWindowToQB: (isEditFlow: boolean) => void,
        getDataFromConnectionWindowToQb: (isEditFlow: boolean, data: IQbLoginData) => void,
        getConnection: () => void,
        sendData: () => void,
        removeConnection: () => void,
        getExternalAccounts: (accountsSystemList: IAccountSystemList[]) => void,
        checkInputForUnique: (changedValue: string) => ng.IPromise<number>,
        goToPreviousStep: () => void,
        cancel: () => void,
        finish: () => void
    }

    class MainController implements IAccountsyncMainController {
        /* Screen's Options*/
        public screenTitle: string = '';
        public currentScreen: MainTabScreens = MainTabScreens.Welcome;
        public currentConnectionFormStep: ConnectionFormSteps = ConnectionFormSteps.ConnectionCredentials;
        public lastConnectionFormStep: ConnectionFormSteps = ConnectionFormSteps.ReadyToSend;

        /* Data stores for connection */
        public latestConnectionId: Guid;
        public selectedConnectionListItem: IConnectionListItem;
        public selectedConnection: IConnectionSetupInfo & IAccountsSetup = null;
        public selectedConnectionBackup: IConnectionSetupInfo & IAccountsSetup = null;

        /* Different types of lists */
        public connectionList: IConnectionListItem[];  
        public accountSystemList: IAccountSystemList[][];
        public defaultAcountsList: IAccountInfo[] = [];
        public externalAccountList: IAccountInfo[][] = [];
        public paymentAccountList: IPaymentAccount[][] = [];
        public paymentTypes: Meevo.Model.PaymentTypeListItem[];
        public accountsRetentionStatus: Array<IAccountsRetentionStatus> = [];

        /* Flags for UI management */
        public isDisableControlButtons: boolean = true;
        public isRemoveSuccessful: boolean = false;
        public isRemovedFirstConnection: boolean = false;
        public isAccountOptionsSavedSuccessfully: boolean = false;
        public isShowConnectionCredentials: boolean = true;
        public isSuccessfullyRefreshedToken: boolean = false;
        public isSuccessfullyAuthInQb: boolean = false;
        public isRealmIdUnique: boolean = true;
        

        /* Others */
        public defaultPaymentAccountName: string = 'Undeposited Funds';
        public defaultAccountCountRequestBody: IAccountCountRequest = {
            criteria: {
                displayName: null,
                url: null,
                realmId: null,
                objectState: ObjectStateEnum.Active,
                filter: null,
                maintenanceMode: true,
                isGlobal: true
            },
            asLocationId: this.identityService.locationId  
        };
        public toolTips: IToolTip[] = [
            { value: ToolTipEnum.ProductIncome, template: 'app/plugins/accountsync/components/tooltip/tooltipViews/productIncome.html' },
            { value: ToolTipEnum.ServiceIncome, template: 'app/plugins/accountsync/components/tooltip/tooltipViews/serviceIncome.html' },
            { value: ToolTipEnum.OtherIncome, template: 'app/plugins/accountsync/components/tooltip/tooltipViews/otherIncome.html' },
            { value: ToolTipEnum.AccountsReceivable, template: 'app/plugins/accountsync/components/tooltip/tooltipViews/accountsReceivable.html' },
            { value: ToolTipEnum.InventoryAssets, template: 'app/plugins/accountsync/components/tooltip/tooltipViews/inventoryAssets.html' },
            { value: ToolTipEnum.CostofGoodsSoldAccount, template: 'app/plugins/accountsync/components/tooltip/tooltipViews/costOfGoodsSoldAccount.html' },
            { value: ToolTipEnum.Liabilities, template: 'app/plugins/accountsync/components/tooltip/tooltipViews/liabilities.html' },
            { value: ToolTipEnum.ExpenseAccount, template: 'app/plugins/accountsync/components/tooltip/tooltipViews/expenseAccount.html' }
        ];
        public disabledPaymentType = 'Cash';
        public cashDefaultOption = 'Meevo Cash Drawer';
        private qbAuthTab: any;

        static $inject = [
            'meevo.AppSettings',
            'meevo.components.modal.ModalService',
            'meevo.components.common.IdentityService',
            'meevo.plugins.accountsync.AccountsyncService',
            "meevo.components.busy.BusyService",
            '$rootScope',
            '$scope',
            '$state',
            '$translate',
            '$filter',
            '$q'
        ];

        constructor(protected appSettings: Meevo.Components.Common.IAppSettings,
            protected modalService: Meevo.Components.Modal.IModalService,
            protected identityService: Meevo.Components.Common.IIdentityService,
            protected accountsyncService: IAccountsyncService,
            protected busyService: Meevo.Components.Busy.IBusyService,
            protected $rootScope: angular.IRootScopeService,
            protected $scope: angular.IScope,
            protected $state: any,
            protected $translate: angular.translate.ITranslateService,
            protected $filter: angular.IFilterService,
            protected $q: angular.IQService
        ) {
        }

        $onInit(): void {
            this.resetFormScreen();

            // Redirect from delete template in main tab to welcome tamplate If deleted all connections
            this.$scope.$watch(() => this.connectionList, (newValue: Array<IConnectionListItem>, oldValue: Array<IConnectionListItem>) => {
                if (this.currentScreen === MainTabScreens.DeleteConnection && !newValue.length) {
                    this.finish();
                }
                //
                if (this.currentScreen === MainTabScreens.DeleteConnection && newValue.length) {
                    this.selectedConnectionListItem = this.connectionList[0];
                }
            });   

            this.$scope.$on('$mdMenuOpen', () => {
                this.stylizationTooltip();
            });
        }

         /**
         * Show the user a specific screen in the Main tab
         * @param {MainTabScreens} screen
         */
        showScreen(screen: MainTabScreens): void {
            switch (screen) {
                case MainTabScreens.Welcome:
                    this.screenTitle = '';
                    break;
                case MainTabScreens.ConnectionForm:
                    //this.resetFormScreen();                    
                    this.isDisableControlButtons = true;
                    this.isShowConnectionCredentials = true;
                    this.isSuccessfullyAuthInQb = false;
                    this.screenTitle = this.translate('LABEL_CREATE_CONNECTION');
                    break;
                case MainTabScreens.DeleteConnection:
                    this.screenTitle = this.translate('LABEL_ACCOUNTSYNC_MAIN_TITLE_REMOVE');

                    this.busyService.start();
                    this.updateConnectionList().finally(() => {
                        this.busyService.stop();
                    });
                    break;
                case MainTabScreens.EditConnection:                    
                    this.screenTitle = this.translate('LABEL_ACCOUNTSYNC_MAIN_TITLE_EDIT');
                    this.isDisableControlButtons = true;
                    this.isShowConnectionCredentials = false;
                    this.setDefaultConnectionOrExistingInEdit();
                    break;
            }
            this.currentScreen = screen;
        }

        /**
         * Changed view when connection is created or edited
         */
        goToNextStep() {
            this.changeStepHandler().then((isNextStepAllowed) => {
                 // Go to next page after success
                if (isNextStepAllowed) this.currentConnectionFormStep++;
            })
        }

        /**
         * Prepare data for the next step and handle current
         */
        changeStepHandler(): ng.IPromise<boolean> {

            switch (this.currentConnectionFormStep) {
                case ConnectionFormSteps.ConnectionCredentials:
                    this.isDisableControlButtons = true;
                    this.busyService.start();

                    return this.saveConnectionInfo().then(() => {
                        return this.accountsyncService.getAccountSystemList();
                    }).then((accountsSystemList: IAccountSystemList[]) => {
                        this.accountSystemList = this.prepareAccountSystemLists(accountsSystemList);
                        this.defaultAcountsList = this.getDefaultAccountsList(accountsSystemList);
                        return this.getExternalAccounts(accountsSystemList);
                    }).then((externalAccountList: IAccountInfo[][]) => {                       
                        this.externalAccountList = externalAccountList;
                        return true;
                    }).catch((error: any) => {
                        this.isDisableControlButtons = true;
                        this.selectedConnection.applicationConnection.undepositedFundsAccountExternalId = null;
                        return false;
                    }).finally(() => {
                        this.busyService.stop();
                        this.isDisableControlButtons = false;
                    });

                    
                case ConnectionFormSteps.AccountSetup:
                    this.isDisableControlButtons = true;
                    this.busyService.start();

                    return this.getPaymentTypeList().then(() => {
                        return this.getPaymentTypeAccounts().then(_=> true);
                    }).finally(() => {
                        this.busyService.stop();
                        this.isDisableControlButtons = false;
                    });

                  
                case ConnectionFormSteps.PaymentTypeSetup:
                    if (this.selectedConnection.applicationConnection.includeMonetaryPayments) return this.$q.resolve(true);

                    let options: Meevo.Components.Modal.IModalOptions = this.getDefaultModalOptions('MESSAGE_YOU_TURNED_OFF_MONETARY_PAYMENTS', Meevo.Components.Modal.ModalTypeEnum.Warning, true, { showCancelButton: true })

                    return this.modalService.showCustom(options).then((canGoNext: boolean) => {
                        return canGoNext ? this.modalService.message('MESSAGE_ABOUT_FUTURE_MONETARY_PAYMENTS_TURN_ON').then(_ => true) : false;
                    });
                
            }      
        }        

        /**
         * Logic for Back button
         */
        goToPreviousStep(): void {            
            this.currentConnectionFormStep--;
            if (this.currentConnectionFormStep === ConnectionFormSteps.ConnectionCredentials) {
                this.showScreen(this.currentScreen);
            }            
        }

        /**
         * Get choosed account for system accountss
         * @param {IAccountSystemList} accountType
         * @returns {string}
         */
        getSelectedAccount(accountType: IAccountSystemList): string {
            let chosenAccount: IAccountInfo;
            if (this.selectedConnectionBackup.accountSetup.accounts[accountType.id - 1]) {
                chosenAccount = this.selectedConnectionBackup.accountSetup.accounts[accountType.id - 1];
            // Check if default account present in External Account List.
            // It can watch by property accountExternalId. If default account doesn't present in list accountExternalId = '0'
            // If default account present in External Account List - this account was already created and we must show him
            } else if (this.defaultAcountsList[accountType.id - 1] && +this.defaultAcountsList[accountType.id - 1].accountExternalId) {
                chosenAccount = this.defaultAcountsList[accountType.id - 1];
            }

            const accountDisplayName = (chosenAccount: IAccountInfo): string => {
                // Look if attached account in external acount list
                const externalAccount = _.find(this.externalAccountList[accountType.id - 1], (externalAccount: IAccountInfo) => {
                    return externalAccount.accountExternalId === chosenAccount.accountExternalId;
                });

                // If it not there so show name from connection info
                return externalAccount && externalAccount.accountExternalDisplayName || chosenAccount.accountExternalDisplayName;
            };

            return chosenAccount ? accountDisplayName(chosenAccount) : this.translate('TEXT_NO_ACCOUNT_SELECTED');
        }

        /**
         * Open connection window for connection to QB
         */
        openConnectionWindowToQB(isEditFlow: boolean): void {
            const url = '/assets/pages/accountSyncLogin.html?l='+this.identityService.locationId+'&t='+this.identityService.current().tenantId;
            this.qbAuthTab = window.open(url, 'QuickBooks Login', 'menubar=yes,location=yes,resizable=yes,scrollbars=yes,status=yes');
            
            window['meevoCallbacks'] = {};
            window['meevoCallbacks']['updateAccountSyncData'] = this.getDataFromConnectionWindowToQb.bind(this, isEditFlow);
            window['meevoCallbacks']['processingErrorCode'] = this.processingErrorCodeFromQb.bind(this);
            window['meevoCallbacks']['bearerToken'] = window['bearerToken'];
        }

        /**
         * Callback for processing information from connection window
         * @param {IQbLoginData} qbData
         */
        getDataFromConnectionWindowToQb(isEditFlow: boolean, qbData: IQbLoginData): void {
            this.resetQbAuthWindow();
            this.isSuccessfullyAuthInQb = true;
            this.selectedConnection.applicationConnectionDetail.updateAuthentificatedOnTime = true;

            // If RealmId the same - don't check for unique
            //if (qbData.realmId === this.selectedConnection.applicationConnectionDetail.realmId) {
            //    this.isDisableControlButtons = false;
            //    this.$scope.$apply();
            //} else {
            const updateConnectionDetail = () => {
                this.isDisableControlButtons = false;
                this.isRealmIdUnique = true;

                _.assign(this.selectedConnection.applicationConnectionDetail, {
                    realmId: qbData.realmId,
                    companyName: qbData.realmId || '',
                    active: qbData.active,
                    accessToken: qbData.accessToken,
                    refreshToken: qbData.refreshToken,
                    qbUserName: qbData.user ? qbData.user.familyName + qbData.user.givenName : qbData.realmId
                });
            }

            if (isEditFlow) return updateConnectionDetail();

            this.busyService.start();
            const requestBody = angular.copy(this.defaultAccountCountRequestBody);
            requestBody.criteria.realmId = qbData.realmId;

            this.accountsyncService.getConnectionsCount(requestBody).then((count: number) => {
                if (count) {
                    this.isDisableControlButtons = true;
                    this.isRealmIdUnique = false;
                    return;
                }

                updateConnectionDetail();

            }).finally(() => {
                this.busyService.stop();
            });

            //}

        }

        /**
         * Callback for processing error code from QB window
         * @param {string} errorCode
         */
        processingErrorCodeFromQb(errorCode: string): void {
            this.resetQbAuthWindow();
            this.modalService.alert(this.translate('MESSAGE_ACCOUNTSYNC_ERROR_FROM_QB', { errorCode: errorCode }));            
        }

        /**
         * Get information about connection by clicking on it in Select on EditConnection view
         */
        getConnection(): void {
            this.busyService.start();
            this.isShowConnectionCredentials = true;

            this.accountsyncService.getConnection(this.selectedConnectionListItem.applicationConnectionId)
                .then((data: IConnectionSetupInfo & IAccountsSetup) => {
                    data.accountSetup.accounts = this.$filter<any>('orderBy')(data.accountSetup.accounts, 'accountSId');
                    this.selectedConnection = angular.copy(data);
                    this.selectedConnectionBackup = angular.copy(data);

                    return this.accountsyncService.updateAccessToken({
                        applicationConnectionId: data.applicationConnection.applicationConnectionId,
                        applicationConnectionDetailId: data.applicationConnectionDetail.applicationConnectionDetailId
                    });
                }).then((connectionCreadentials: IQbLoginData) => {
                    this.selectedConnection.applicationConnectionDetail.accessToken = connectionCreadentials.accessToken;
                    this.isDisableControlButtons = false;
                    this.isSuccessfullyRefreshedToken = true;
                }).finally(() => {
                    this.busyService.stop();
                });
        }

        /**
         * Adding accounts and payments to connection
         */
        sendData(): void {
            this.isDisableControlButtons = true;
            this.busyService.start();

            // isActive must be true when we saved accounts and payments for connection
            if (!this.selectedConnection.applicationConnection.isActive) {
                this.selectedConnection.applicationConnection.isActive = true;
            }

            this.accountsyncService.getTaxStatus({
                applicationConnectionId: this.selectedConnection.applicationConnection.applicationConnectionId
            }).then((taxStatus: boolean) => {
                if (!taxStatus) {
                    this.modalService.alert('MESSAGE_ALERT_TAX_MUST_TRNED');
                    return this.$q.reject();
                }
                return this.accountsyncService.addAccountsToConnection(this.selectedConnection);
            }).then(response => {
                // To get id of connection
                this.setLatestConnectionId({ connectionId: response.applicationConnectionId });
                this.isAccountOptionsSavedSuccessfully = true;

                this.accountsRetentionStatus = response.items;
                this.updateConnectionList();
            }).catch(() => {
                this.isDisableControlButtons = false;
            }).finally(() => {
                this.busyService.stop();
            });
        }

        /**
         * Remove connection using it's id
         */
        removeConnection(): void {
            const message: string = this.translate('MESSAGE_CONFIRM_DELETE_CONNECTION', { connectionName: this.selectedConnectionListItem.applicationConnectionDisplayName });

            this.modalService.confirm(message).then(() => {
                this.accountsyncService.deleteConnection({
                    id: this.selectedConnectionListItem.applicationConnectionId,
                    objectStateEnum: ObjectStateEnum.Deleted,
                    entityVersion: this.selectedConnectionListItem.entityVersion
                }).then(() => {
                    this.isRemoveSuccessful = true;
                    this.isRemovedFirstConnection = true;
                    this.selectedConnectionListItem = null;

                    this.setLatestConnectionId(null);

                    this.busyService.start();
                    this.updateConnectionList().finally(() => {
                        this.busyService.stop();
                    });
                });
            });
        }

        /**
         * Get and prepare external accounts for the select when opening a select on the AccountSetup view
         * @param {IAccountSystemList[]} accounts
         * @returns {ng.IPromise<any>} promise
         */
        getExternalAccounts(accountsSystemList: IAccountSystemList[]): ng.IPromise<IAccountInfo[][]> {
            const externalAccountList: IAccountInfo[][] = []; 

            return this.getExernalAccountsList(accountsSystemList.map(account => account.id)).then((externalAccounts: IExternalAccount[]) => {
                if (externalAccounts) {                    
                    externalAccounts.forEach((externalAccount: IExternalAccount) => {
                        // If default account is exist in External account list then collapse them to one
                        if (this.defaultAcountsList[externalAccount.accountType - 1] && this.defaultAcountsList[externalAccount.accountType - 1].accountExternalDisplayName === externalAccount.fullName) {
                            this.defaultAcountsList[externalAccount.accountType - 1].accountExternalId = externalAccount.id;
                            return;
                        }

                        if (!externalAccountList[externalAccount.accountType - 1]) {
                            externalAccountList[externalAccount.accountType - 1] = [];
                        }

                        const accountSystem = _.find(accountsSystemList, (account: IAccountSystemList) => account.id === externalAccount.accountType);
                        accountSystem && externalAccountList[externalAccount.accountType - 1].push(this.prepareOptionForSelect(accountSystem, externalAccount));
            });
        }

                return externalAccountList;
            });
        }

        /**
         * Function for checking the uniqueness of the input, which is passed to the local directive
         * @param {string} changedValue
         * @returns {ng.IPromise<number>} promise
         */
        checkInputForUnique(changedValue: string): ng.IPromise<number> {
            if (changedValue !== '' && changedValue !== null && changedValue !== this.selectedConnectionBackup.applicationConnection.applicationConnectionDisplayName) {
                const requestBody = angular.copy(this.defaultAccountCountRequestBody);
                requestBody.criteria.displayName = changedValue;
                return this.accountsyncService.getConnectionsCount(requestBody);
            } else if (changedValue === this.selectedConnectionBackup.applicationConnection.applicationConnectionDisplayName) {
                return this.$q((resolve, reject) => {
                    resolve(0);
                });
            }
        }
         /**
         * Cancel editing or creating connection
         */
        cancel() {
            this.modalService.alert("MESSAGE_ARE_YOU_SURE_YOU_WAN_TO_CANCEL_CLICK_YES", Meevo.Components.Modal.ModalTranslationModes.Translate, false, { showCancelButton: true, cancelButtonContentKey: 'LABEL_NO', cancelButtonHotKey: 'MODAL_NO_BUTTON', okButtonHotKey: 'MODAL_YES_BUTTON', okButtonContentKey: 'LABEL_YES' })
                .then((response: boolean) => {
                    this.resetFormScreen();
                    this.showScreen(MainTabScreens.Welcome);
                });
        }

        /**
         * Finish creating, editing or deleting connection
         */
        finish(currentConnectionFormStep?: ConnectionFormSteps) {
            if (currentConnectionFormStep === this.lastConnectionFormStep) {
                this.openSyncTab();
            }

            this.resetFormScreen();
            this.showScreen(MainTabScreens.Welcome);            
        }

        /**
         * Save connection info namely name and information from window connection to QB
         */
        private saveConnectionInfo(): ng.IPromise<any> {
            return this.accountsyncService.testUndepositedFundsAccount({
                realmId: this.selectedConnection.applicationConnectionDetail.realmId,
                accessToken: this.selectedConnection.applicationConnectionDetail.accessToken
            }).then((response: any) => {
                if (this.currentScreen !== MainTabScreens.EditConnection) {
                    this.selectedConnection.applicationConnection.undepositedFundsAccountExternalId = response.id;
                }

                this.defaultPaymentAccountName = response.name;
                return this.setupConnection(this.selectedConnection);
            }).then(resp => {
                if (this.currentScreen === MainTabScreens.ConnectionForm) {
                    this.selectedConnection.applicationConnection = resp.applicationConnection;
                    this.selectedConnection.applicationConnectionDetail = resp.applicationConnectionDetail;
                }

                this.updateConnectionList();
            });
        }

        /**
         * Return default accounts list for Account Setup dropdowns
         * @param {IAccountSystemList[]} accountSystemLists
         * @returns {IAccountInfo}
         */
        private getDefaultAccountsList(accountSystemLists: IAccountSystemList[]): IAccountInfo[] {
            const defaultAccountsList: IAccountInfo[] = [];
            accountSystemLists.forEach((accountType: IAccountSystemList) => {
                defaultAccountsList.push(this.prepareOptionForSelect(accountType));
            });
            return defaultAccountsList;
                    }

        /**
         * Get payments type list
         * @returns {ng.IPromise<any>} promise
         */
        private getPaymentTypeList(): ng.IPromise<any> {
            let requestObj = { criteria: { includeNonMonetary: 0, IncludeInactive: 1, IncludeDeleted: 1, IncludeActive: 1 }, pageNumber: 0, itemsPerPage: 1000 };

            return this.accountsyncService.getPaymentTypeList(requestObj)
                .then(response => {
                    this.paymentTypes = response;
                    var payment = this.paymentTypes.find(p => p.paymentTypeSId == 25);
                    if (payment != null) {
                        payment.displayName = "Care Credit Integration®";
                    }

                    if (this.currentScreen === MainTabScreens.EditConnection) {
                        let arr = [];
                        this.selectedConnection.paymentTypeSetup.items = this.selectedConnectionBackup.paymentTypeSetup.items;

                        this.paymentTypes.forEach((value: any, i) => {
                            let result = _.find(this.selectedConnection.paymentTypeSetup.items, (item: any) => {
                                return item.paymentTypeId == value.id;
                            });
                            if (result != undefined) {
                                arr[i] = result;
                            }
                        });
                        this.selectedConnection.paymentTypeSetup.items = arr;
                    }
                }
            );
        }

        /**
         * Get and prepare payment type variants
         * @returns {ng.IPromise<any>} promise
         */
        private getPaymentTypeAccounts(): ng.IPromise<any> {            
            return this.getExernalAccountsList([
                ExternalAccountTypeEnum.Bank,
                ExternalAccountTypeEnum.ProductIncome,
                ExternalAccountTypeEnum.ServiceIncome,
                ExternalAccountTypeEnum.Income,
                ExternalAccountTypeEnum.OtherCurrentAsset,
                ExternalAccountTypeEnum.OtherAsset,
                ExternalAccountTypeEnum.FixedAsset,
            ]).then((paymentAccounts: IExternalAccount[]) => {
                this.paymentTypes.forEach((paymentType: Meevo.Model.PaymentTypeListItem, index: number) => {
                    if (paymentAccounts) {
                        this.paymentAccountList[index] = paymentAccounts.map((paymentAccount: IExternalAccount) => {
                            return this.getPaymentAccount(index, paymentType, paymentAccount);
                        });

                        if (paymentType.displayName === this.disabledPaymentType) {
                            const isCashDefaultOptionExist = this.paymentAccountList[index].some((paymentAccount) => {
                                return paymentAccount.paymentTypeAccountExternalDisplayName === this.cashDefaultOption
                            });

                            if (!isCashDefaultOptionExist) {
                                this.paymentAccountList[index].push(
                                    {
                                        paymentTypeAccountExternalDisplayName: this.cashDefaultOption,
                                        paymentTypeAccountExternalId: null,
                                        paymentTypeId: this.paymentAccountList[index][0].paymentTypeId,
                                        paymentTypeName: this.paymentAccountList[index][0].paymentTypeName,
                                        paymentTypeSId: this.paymentAccountList[index][0].paymentTypeSId
                                    }
                                )
                            }
                        }

                    } else {
                        this.paymentAccountList[index] = [];
                    }

                    // Default value -- 'Undeposited Funds'
                    const existDefaultPaymentAccount = _.find(this.paymentAccountList[index], paymentAccount => (
                        paymentAccount.paymentTypeAccountExternalId === this.selectedConnection.applicationConnection.undepositedFundsAccountExternalId)
                    );

                    if (!existDefaultPaymentAccount) {
                        this.paymentAccountList[index].push(this.getPaymentAccount(index, paymentType));
                    }
                });
            });
        }        

        /**
         * Prepare system's accounts for AccountSetup view
         * @param {IAccountSystemList[]} accountSystemLists
         * @returns {IAccountSystemList[][]}
         */
        private prepareAccountSystemLists(accountSystemLists: IAccountSystemList[]): IAccountSystemList[][] {
            const preparedAccountSystemLists: IAccountSystemList[][] = [];
            let tempArray: IAccountSystemList[] = [];

            accountSystemLists.forEach((accountType: IAccountSystemList, index: number, array: IAccountSystemList[]) => {
                tempArray.push(accountType);
                if (tempArray.length === 3 || array.length - 1 == index) {
                    preparedAccountSystemLists.push(tempArray);
                    tempArray = [];
                }
            });

            return preparedAccountSystemLists;
        }

        /**
         * Prepare external account for select in AccountSetup view
         * @param {IAccountSystemList} accountType
         * @param {IExternalAccount} externalAccount
         * @returns {IAccountSetupInfo}
         */
        private prepareOptionForSelect(accountType: IAccountSystemList, externalAccount?: IExternalAccount): IAccountInfo {
            const accountName = externalAccount && externalAccount.name || accountType.defaultAccountName;
            const accountFullName = externalAccount && (externalAccount.fullName || externalAccount.name) || accountType.defaultAccountName;
            const defaultSelectOption: IAccountInfo = {
                accountSId: accountType.id,
                accountSDisplayName: accountType.displayName,
                accountExternalDisplayName: accountName,
                accountExternalFullName: accountFullName,
                accountExternalId: externalAccount && externalAccount.id || '0'
            };

            if (this.currentScreen === MainTabScreens.EditConnection && this.selectedConnection.accountSetup.accounts[accountType.id - 1]) {
                _.assign(defaultSelectOption, this.getExpandedPropertiesForAccount(this.selectedConnection.accountSetup.accounts[accountType.id - 1], false));
            }

            return defaultSelectOption;
        }

        /**
         * Prepare payment account for dropdown in Payment setup
         * @param {number} paymentTypeIndex
         * @param {Meevo.Model.PaymentTypeListItem} paymentType
         * @param {IExternalAccount} paymentAccount
         * @returns {IPaymentAccount}
         */
        private getPaymentAccount(paymentTypeIndex: number, paymentType: Meevo.Model.PaymentTypeListItem, paymentAccount?: IExternalAccount): IPaymentAccount {
            const paymentAccountDefault: IPaymentAccount = {
                paymentTypeAccountExternalId: paymentAccount && paymentAccount.id || this.selectedConnection.applicationConnection.undepositedFundsAccountExternalId,
                paymentTypeAccountExternalDisplayName: paymentAccount && paymentAccount.fullName || this.defaultPaymentAccountName,
                paymentTypeName: paymentType.displayName,
                paymentTypeId: paymentType.id,
                paymentTypeSId: paymentType.paymentTypeSId
            };

            if (this.currentScreen === MainTabScreens.EditConnection && this.selectedConnection.paymentTypeSetup.items[paymentTypeIndex]) {
                _.assign(paymentAccountDefault, this.getExpandedPropertiesForAccount(this.selectedConnection.paymentTypeSetup.items[paymentTypeIndex], true));
            };

            return paymentAccountDefault;
        }

        /**
         * Return extended properties for account
         * @param {IAccountInfo | IPaymentAccount} incomingData
         * @param {boolean} isPaymentAccount watch if add unique property for non-payment account
         * @returns {IAccountInfo | IPaymentAccount}
         */
        private getExpandedPropertiesForAccount(incomingData: IAccountInfo | IPaymentAccount, isPaymentAccount?: boolean): IAccountInfo | IPaymentAccount {
            let expandedProperties: any = {
                applicationConnectionId: incomingData.applicationConnectionId,
                tenantId: incomingData.tenantId,
                entityVersion: incomingData.entityVersion
            };
            if (!isPaymentAccount) expandedProperties.accountId = (<IAccountInfo>incomingData).accountId;

            return expandedProperties;
        }

        /**
         * Get external account using account type id
         * @param {ExternalAccountTypeEnum[]} accountTypeIds
         * @returns {angular.IPromise<IExternalAccount[]>} promise
         */
        private getExernalAccountsList(accountTypeIds: ExternalAccountTypeEnum[]): angular.IPromise<IExternalAccount[]> {
            return this.accountsyncService.getExernalAccountsList({
                types: accountTypeIds,
                tenantId: this.identityService.current().tenantId,
                locationId: this.identityService.locationId,
                applicationConnectionId: this.selectedConnection.applicationConnection.applicationConnectionId,
                applicationConnectionDetailId: this.selectedConnection.applicationConnectionDetail.applicationConnectionDetailId
            });
        }

        /**
         * Save or update connection depending on what action we perform (edit or create connection)
         * @param {any} body
         * @returns {angular.IPromise<any>} promise
         */
        private setupConnection(body: any): angular.IPromise<any> {
            return this.currentScreen === MainTabScreens.EditConnection ?
                this.accountsyncService.updateConnectionInfo(body) :
                this.accountsyncService.saveConnectionInfo(body);
        }

        /**
         * Check if attached account is exist in external account list
         * @param {IAccountSystemList} accountType
         * @returns {boolean}
         */
        private isAttachedAccountInExternalAccountsList(accountType: IAccountSystemList): boolean {
            const attachedAccountId: string = this.selectedConnectionBackup.accountSetup.accounts[accountType.id - 1] &&
                this.selectedConnectionBackup.accountSetup.accounts[accountType.id - 1].accountExternalId || '0';
            const externalAccountsListForSystemAccount: IAccountInfo[] = this.externalAccountList[accountType.id - 1] || [];

            return _.contains(externalAccountsListForSystemAccount.map(externalAccount => externalAccount.accountExternalId), attachedAccountId);
        }

        /**
         * Check if attached payment account is exist in payment account list
         * @param {number} paymentTypeIndex
         * @returns {boolean}
         */
        private isAttachedPaymentAccountInPaymentAccountsList(paymentTypeIndex: number): boolean {
            const attachedPaymentAccountId = this.selectedConnection.paymentTypeSetup.items[paymentTypeIndex] &&
                this.selectedConnection.paymentTypeSetup.items[paymentTypeIndex].paymentTypeAccountExternalId || null;
            const paymentAccountList = this.paymentAccountList[paymentTypeIndex] || [];

            return _.contains(paymentAccountList.map(paymentAccount => paymentAccount.paymentTypeAccountExternalId), attachedPaymentAccountId);
        }

        /**
         * Function for showing alert when input is not unique, which is passed to the local directive
         */
        private showAlertForUniqueInput(): void {
            this.modalService.alert('MESSAGE_ALERT_UNIQUE_CONNECTION_VALUE');
        }

        /**
         * Return tooltip template view usind tooltip's id
         * @param {number} tooltipId
         * @returns {string}
         */
        private getToolTipView(tooltipId: number): string {
            return this.toolTips[tooltipId] && this.toolTips[tooltipId].template || '';
        }

        /**
         * Change flag to remove the message that the connection was successfully deleted when a new connection is selected
         */
        private changedConnectionForDeleting(): void {
            this.isRemoveSuccessful = false;
        }

        /**
         * Set first connection as default in dropdown in Edit view
         */
        private setDefaultConnectionOrExistingInEdit(): void {
            const connectionId = this.selectedConnectionListItem && this.selectedConnectionListItem.applicationConnectionId || '';

            this.selectedConnectionListItem = connectionId ? _.find(this.connectionList, connection => connection.applicationConnectionId === connectionId): this.connectionList[0];
            this.getConnection();
        }

        /**
         * Translate using key
         * @param {string} key
         * @param {any} parameters
         * @returns {string}
         */
        private translate(key: string, parameters?: any): string {
            return this.$translate.instant(key, parameters);
        }

        get mainTabScreensEnum(): any {
            return MainTabScreens;
        }

        get connectionFormStepsEnum(): any {
            return ConnectionFormSteps;
        }

        /**
         * Reset variables values wich using in component
         */
        private resetFormScreen(): void {
            this.selectedConnection = {                
                applicationConnection: {
                    applicationConnectionId: null,
                    applicationConnectionDisplayName: null,
                    applicationConnectionUrl: null,
                    applicationConnectionUserName: null,
                    applicationConnectionPassword: null,
                    undepositedFundsAccountExternalId: null,
                    externalApplicationEnum: ExternalApplicationEnum.Quickbooks,
                    applicationConnectionTypeEnum: ConnectionVersionEnum.Online,
                    objectStateEnum: ObjectStateEnum.Active,
                    isActive: false,
                    entityVersion: 0,
                    isInventoryManagementAllowed: null,
                    includeMonetaryPayments: true
                },
                applicationConnectionDetail: {
                    applicationConnectionDetailId: null,
                    realmId: null,
                    companyName: null,
                    qbUserName: null,
                    active: null,
                    accessToken: null,
                    accessTokenReceivedOn: null,
                    refreshToken: null,
                    refreshTokenReceivedOn: null,
                    authenticatedOn: null,
                    updateAuthentificatedOnTime: null
                },
                accountSetup: {
                    accounts: []
                },
                paymentTypeSetup: {
                    items: []
                }
            };            
            this.isDisableControlButtons = false;
            this.selectedConnectionBackup = angular.copy(this.selectedConnection);
            this.selectedConnectionListItem = null;
            this.isRemoveSuccessful = false;
            this.isRemovedFirstConnection = false;
            this.isSuccessfullyRefreshedToken = false;
            this.isSuccessfullyAuthInQb = false;
            this.isRealmIdUnique = true;
            this.defaultAcountsList = [];
            this.externalAccountList = [];
            this.paymentAccountList = [];
            this.accountsRetentionStatus = [];
            this.defaultPaymentAccountName = 'Undeposited Funds';

            //Default view state
            this.isAccountOptionsSavedSuccessfully = this.isShowConnectionCredentials = false;

            //Reset form screen
            this.currentConnectionFormStep = ConnectionFormSteps.ConnectionCredentials;

            // Close QB auth window if it present
            this.resetQbAuthWindow();
        }

        /**
        * Get the options for the custom modal.
        * @param {string} message
        * @param {ModalTypeEnum} modalType
        * @param {boolean} processNegativeConfirm
        * @param {Object} userOptions
        * @return {IModalOptions}
        */
        private getDefaultModalOptions(message: string, modalType: Meevo.Components.Modal.ModalTypeEnum, processNegativeConfirm: boolean = false, userOptions: Object = undefined): Meevo.Components.Modal.IModalOptions {
            return {
                templateUrl: "app/components/modal/modal.template.html",
                controller: "meevo.components.modal.ModalController",
                controllerAs: "vm",
                bindToController: true,
                preserveScope: true,
                hasBackdrop: false,
                multiple: true,
                locals: {
                    message: this.$translate.instant(message),
                    modalType: modalType,
                    processNegativeConfirm: processNegativeConfirm,
                    isRegister: false,
                    ...{ userOptions }
                }
            }
        }

        // Binded methods
        resetQbAuthWindow(): void {}
        updateConnectionList(): any {}
        openSyncTab(): void {}
        setLatestConnectionId(connectionId: any): void {}
        stylizationTooltip(): void {}
    }

    class MainComponent implements angular.IComponentOptions {
        templateUrl = 'app/plugins/accountsync/components/main/main.template.html';
        controller = MainController;
        controllerAs = 'model';
        bindings: any = {
            latestConnectionId: '@',
            connectionList: '=',
            resetQbAuthWindow: '&',
            updateConnectionList: '&',
            openSyncTab: '&',
            qbAuthTab: '=',
            setLatestConnectionId: '&',
            stylizationTooltip: '&'
        };
    }

    angular.module('meevo.plugins.accountsync')
        .component('accountasyncMainComponent', new MainComponent());
}
